// Per-report and per-action-item comment threads. Cross-section by design —
// the inbox + the section cards both need them.
import { api } from "./client";
import type { Comment } from "./types";

export type Kind =
  | "report"
  | "action-item"
  | "cuec"
  | "exception"
  | "scope-check"
  | "bridge"
  | "subservice"; // #99: per-C6-carve-out thread (keyed by the stable carve-out id)

// Map each kind to its list/create base path and its edit/delete segment.
// #99: carve-out keys can be a name (legacy/manual rows without a minted id),
// so encode the id — an unencoded space/paren in the path broke comment save.
const LIST_BASE: Record<Kind, (id: string) => string> = {
  report: (id) => `/reports/${id}/comments`,
  "action-item": (id) => `/action-items/${id}/comments`,
  cuec: (id) => `/cuecs/${id}/comments`,
  exception: (id) => `/exceptions/${id}/comments`,
  "scope-check": (id) => `/scope-checks/${id}/comments`,
  bridge: (id) => `/bridge/${id}/comments`,
  subservice: (id) => `/carveouts/${encodeURIComponent(id)}/comments`,
};
const SEG: Record<Kind, string> = {
  report: "report",
  "action-item": "action-item",
  cuec: "cuec",
  exception: "exception",
  "scope-check": "scope-check",
  bridge: "bridge",
  subservice: "subservice",
};

export async function listComments(kind: Kind, targetId: string): Promise<Comment[]> {
  const data = await api.get<{ comments: Comment[] }>(LIST_BASE[kind](targetId));
  return data.comments;
}

export async function addComment(kind: Kind, targetId: string, body: string): Promise<Comment> {
  const data = await api.post<{ comment: Comment }>(LIST_BASE[kind](targetId), { body });
  return data.comment;
}

export async function deleteComment(kind: Kind, commentId: string): Promise<void> {
  await api.delete<{ status: string }>(`/comments/${SEG[kind]}/${commentId}`);
}

export async function editComment(kind: Kind, commentId: string, body: string): Promise<Comment> {
  const data = await api.patch<{ comment: Comment }>(`/comments/${SEG[kind]}/${commentId}`, { body });
  return data.comment;
}

/** ID of the signed-in user. Dev mode reads VITE_DEV_USER_ID; in prod
 *  this will read the Cognito identity. Used to decide whether to show
 *  Edit/Delete affordances on a comment. */
export function currentUserId(): string | null {
  return (import.meta.env.VITE_DEV_USER_ID as string | undefined) ?? null;
}
